/**
 * ISSUE-1341. What a pool card opens while the fan's request to join is still
 * waiting: the pool itself (name, league, players, rules in plain words) and
 * Cancel request, v1's Pool Info sheet in its "Request pending" state.
 *
 * The preview is `/pool/join-preview` with the pool's id as `targetPoolId`,
 * which answers a private pool only to a caller with their own pending
 * request in it. Cancel request is `/pool/join-request-cancel`, behind the
 * shared destructive confirm. Both name the pool as `targetPoolId` because
 * the fan is not a member yet, and a `poolId` would be refused at the door.
 */
import {
  usePoolJoinPreview,
  usePoolJoinRequestCancel,
} from "@sportsmet/pool-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  InlineBanner,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useMemo } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { JOIN_OFFLINE_MESSAGE } from "./joinPoolErrorCopy";
import { JoinPoolPreview, JoinPoolPreviewSkeleton } from "./JoinPoolPreview";
import { leagueAbbreviationsByKey } from "./poolsListState";

export const PENDING_POOL_NOTICE =
  "Your request is waiting on the Commissioner. We'll let you know when you're in.";
export const PENDING_POOL_GONE =
  "We couldn't load this pool. Your request may have been answered already.";
export const CANCEL_REQUEST_LABEL = "Cancel request";
export const CANCEL_REQUEST_CONFIRM_BODY =
  "You'll need the invite code again to ask a second time.";

export type typePendingPoolDialogProps = {
  open: boolean;
  poolId: string;
  poolName: string;
  onClose: () => void;
};

export const PendingPoolDialog = (
  props: typePendingPoolDialogProps,
): ReactNode => {
  const preview = usePoolJoinPreview();
  const cancel = usePoolJoinRequestCancel();
  const catalog = useLeagueCatalogGet({});
  const leagueAbbreviations = useMemo(
    () => leagueAbbreviationsByKey(catalog.data?.data?.sports ?? []),
    [catalog.data],
  );

  useEffect(() => {
    if (!props.open) return;
    preview.reset();
    preview.mutate({ targetPoolId: props.poolId });
    // `preview` is a fresh object every render; the open and the pool are
    // the real dependency list.
  }, [props.open, props.poolId]);

  async function handleCancel(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: `Cancel your request to join ${props.poolName}?`,
      body: CANCEL_REQUEST_CONFIRM_BODY,
      confirmLabel: CANCEL_REQUEST_LABEL,
      cancelLabel: "Keep my request",
    });
    if (!isConfirmed) return;
    try {
      const result = await cancel.mutateAsync({ targetPoolId: props.poolId });
      if (showApiError(result)) return;
      props.onClose();
    } catch {
      showOfflineError();
    }
  }

  const data = preview.data?.data ?? null;
  let body: ReactNode;
  if (preview.isError) {
    body = <InlineBanner variant="warning" message={JOIN_OFFLINE_MESSAGE} />;
  } else if (preview.data?.error != null) {
    body = <InlineBanner variant="warning" message={PENDING_POOL_GONE} />;
  } else if (data === null) {
    body = <JoinPoolPreviewSkeleton />;
  } else {
    body = (
      <>
        <JoinPoolPreview
          model={{
            poolId: data.pool.poolId,
            name: data.pool.name,
            leagueKey: data.pool.leagueKey,
            contestType: data.pool.contestType,
            activeMemberCount: data.pool.activeMemberCount,
            rulesSummary: data.rulesSummary,
          }}
          leagueAbbreviations={leagueAbbreviations}
        />
        {data.commissionerName === undefined ? null : (
          <p className="text-caption text-ink-secondary">
            {`Run by ${data.commissionerName}`}
          </p>
        )}
      </>
    );
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      title={props.poolName}
      description="Request pending"
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            Close
          </Button>
          <Button
            variant="destructive"
            loading={cancel.isPending}
            onClick={() => void handleCancel()}
            data-testid="pending-pool-cancel"
          >
            {CANCEL_REQUEST_LABEL}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="pending-pool-dialog">
        <InlineBanner variant="neutral" message={PENDING_POOL_NOTICE} />
        {body}
      </div>
    </Dialog>
  );
};
